import { useMemo } from 'react';

import { METRIC } from '@/entities/insight/index';
import { PostFilter } from '../../../../entities/post/models';
import { useUnifiedThemeTableController } from '../hooks/use-unified-theme-table-controller';
import { ThemeTableHeader } from './theme.table.header';
import { UnifiedThemeTable } from './unified-theme.table';

import { Tabs, TabsList, TabsTrigger } from '@/shared/components/shadcn/tabs';
import { useThemes } from '../../shared/hooks/use-themes';
import { usePublicFilter } from '../../shared/public-filter/use-public-filter';

interface Props {
  startDate: string;
  endDate: string;
  queryId: string;
}

export function UnifiedThemeTableSection({ startDate, endDate, queryId }: Props) {
  const {
    state,
    setSelectedTab,
    setMetric,
    getCurrentLevel,
    toPrevDepth,
    handleRowClick,
    handleDrillDown,
    getCurrentSentiment,
    filter,
  } = useUnifiedThemeTableController();

  const { selectedTab, depths, metric } = state;
  const currentLevel = getCurrentLevel();

  // usePublicFilter를 Suspense 밖에서 호출하여 무한 루프 방지
  const { publicFilter } = usePublicFilter(queryId);

  // Determine viewBy based on current level
  const viewBy = useMemo(() => {
    if (currentLevel === 'theme') return PostFilter.Theme;
    if (currentLevel === 'brand') return PostFilter.Brand;
    return PostFilter.Product;
  }, [currentLevel]);

  // Fetch data
  const { data: themes, isLoading } = useThemes({
    queryId,
    startDate,
    endDate,
    sentiment: getCurrentSentiment(),
    view_by: viewBy,
    theme: filter.theme,
    brand: filter.brand,
    limit: 100,
    publicFilter,
  });

  return (
    <div className="flex flex-col gap-6 rounded-lg border shadow-sm">
      {/* 헤더 영역 - Suspense 밖에 위치하여 항상 유지 */}
      <div className="flex items-center justify-between p-6 pb-0">
        <div className="flex items-center gap-3">
          <ThemeTableHeader
            level={currentLevel}
            selectedTab={selectedTab}
            onTabChange={setSelectedTab}
            depths={depths}
            onBack={toPrevDepth}
          />
        </div>
        <Tabs value={metric} onValueChange={(value) => setMetric(value as METRIC)}>
          <TabsList>
            <TabsTrigger value={METRIC.MENTION}>{METRIC.MENTION}</TabsTrigger>
            <TabsTrigger value={METRIC.POST}>{METRIC.POST}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* 테이블 영역 - 별도 Suspense 경계로 분리 */}
      <UnifiedThemeTable
        isLoading={isLoading}
        currentLevel={currentLevel}
        selectedTab={selectedTab}
        metric={metric}
        themes={themes || []}
        handleRowClick={handleRowClick}
        handleDrillDown={handleDrillDown}
      />
    </div>
  );
}
